/**
 * Elite Batch 24 Part 1 — Sales & CRM Workflows (1-10)
 *
 * PRODUCTION STANDARD — Every workflow includes:
 * 1. Case stamping (node 2) — unique case_id, timestamps, workflow version
 * 2. Confidence-gated AI — aiNodeV2 with confidence_score + evidence output
 * 3. Confidence routing — confidenceGate splits auto vs human review
 * 4. Post-execution verification — verifyNode reads back from target system
 * 5. Audit logging — sheetsNode logs every case outcome
 *
 * CORRECT FIELD NAMES: conditionData, approvalGateData, delayAction, code
 * Webhook triggers have EMPTY inputFields.
 */

// ============ BASE HELPERS ============

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}

// ============ ENHANCED V2 HELPERS ============

function aiNodeV2(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  const enhancedSystem =
    systemPrompt +
    " Also include in your JSON response: confidence_score (0-100 integer rating your certainty), evidence_summary (one sentence citing the key data points you relied on), and needs_human_review (boolean, true if you are uncertain or the case is unusual).";
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${enhancedSystem}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "choices[0].message.content.confidence_score",
            customName: "ai_confidence",
            type: "number",
          },
          {
            name: "choices[0].message.content.evidence_summary",
            customName: "ai_evidence",
            type: "string",
          },
          {
            name: "choices[0].message.content.needs_human_review",
            customName: "ai_needs_review",
            type: "string",
          },
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

function caseStampNode(
  id: string,
  label: string,
  y: number,
  workflowName: string,
  x = 80,
): any {
  return n(
    id,
    "runCode",
    label,
    y,
    {
      code: `// Stamp case with unique ID, timestamp, and workflow version
const ts = Date.now();
const rand = Math.random().toString(36).substring(2, 8);
output.case_id = 'case_' + ts + '_' + rand;
output.workflow_name = '${workflowName}';
output.workflow_version = '1.0';
output.started_at = new Date(ts).toISOString();
output.source_channel = variables.source_channel || 'webhook';
output.status = 'intake_complete';`,
    },
    x,
  );
}

function confidenceGate(
  id: string,
  label: string,
  y: number,
  threshold = "75",
  x = 80,
): any {
  return n(
    id,
    "condition",
    label,
    y,
    {
      conditionData: [
        {
          variable: "ai_confidence",
          value: threshold,
          comparison_operator: "GREATER THAN",
          logical_operator: "AND",
        },
      ],
    },
    x,
  );
}

function conditionNode(
  id: string,
  label: string,
  y: number,
  variable: string,
  operator: string,
  value: string,
  x = 80,
): any {
  return n(
    id,
    "condition",
    label,
    y,
    {
      conditionData: [
        {
          variable,
          value,
          comparison_operator: operator,
          logical_operator: "AND",
        },
      ],
    },
    x,
  );
}

function approvalNode(
  id: string,
  label: string,
  y: number,
  message: string,
  timeoutHours = 24,
  x = 80,
): any {
  return n(
    id,
    "approvalGate",
    label,
    y,
    {
      approvalGateData: {
        approvers: [],
        message,
        channel: "email",
        timeoutHours,
      },
    },
    x,
  );
}

function delayNode(
  id: string,
  label: string,
  y: number,
  time: number,
  timeFormat: string,
  x = 80,
): any {
  return n(
    id,
    "delayAction",
    label,
    y,
    {
      delayAction: { time, timeFormat },
    },
    x,
  );
}

function verifyNode(
  id: string,
  label: string,
  y: number,
  method: string,
  url: string,
  headers: Array<{ key: string; value: string }>,
  expectedVars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url,
        headers,
        body: [],
        variables: [
          ...expectedVars,
          { name: "status_code", customName: "verify_status", type: "number" },
        ],
      },
    },
    x,
  );
}

function exceptionNode(id: string, label: string, y: number, x = 80): any {
  return n(
    id,
    "runCode",
    label,
    y,
    {
      code: `// Exception handler: categorize, log, and route
const errorType = variables.error_message || variables.verify_status || 'unknown';
const isTransient = ['timeout', '429', '503', '502'].some(e => String(errorType).includes(e));
const retryCount = parseInt(variables.retry_count || '0');
output.exception_type = isTransient ? 'transient' : 'permanent';
output.should_retry = isTransient && retryCount < 3 ? 'yes' : 'no';
output.retry_count = String(retryCount + 1);
output.exception_summary = 'Error: ' + errorType + ' | Type: ' + output.exception_type + ' | Retry: ' + output.should_retry;
output.escalate_to_human = (!isTransient || retryCount >= 3) ? 'yes' : 'no';
output.logged_at = new Date().toISOString();`,
    },
    x,
  );
}

function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}

function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}

function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}

function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// ============ PART 1 — SALES & CRM (10 workflows) ============

export const ELITE_BATCH24_PART1: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1. Elite: Web Form Lead Capture + Scoring
  // ===================================================================
  "Elite: Web Form Lead Capture + Scoring": {
    nodes: [
      trigger("Webhook: New web form submission received", "webhook_form", {
        source: "Website",
        event: "form.submitted",
        howItStarts:
          "Fires when a prospect submits a contact or demo form on the website, capturing all form fields via webhook",
        setup:
          "Configure your web form (Outgrow Forms, HubSpot Forms, or custom) to POST the submission payload to this webhook URL. Ensure fields include email, name, company, and message.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp Case: Generate case_id and intake timestamp",
        240,
        "Web Form Lead Capture + Scoring",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Extract form fields + qualify lead fit",
        480,
        "You are a B2B lead qualification expert. Parse the raw form submission and extract structured contact details. Score the lead based on company size signals, role seniority, and message intent. Return a lead_score from 0-100.",
        "Parse this web form submission: {{{1.body}}}. Return JSON with: contact_email, contact_name, company_name, job_title, message_summary, lead_score (0-100), qualification_tier (hot/warm/cold), buying_signal (string describing purchase intent).",
        [
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "message_summary", as: "message_summary", type: "string" },
          { field: "lead_score", as: "lead_score", type: "number" },
          {
            field: "qualification_tier",
            as: "qualification_tier",
            type: "string",
          },
          { field: "buying_signal", as: "buying_signal", type: "string" },
        ],
      ),

      n("4", "hitApi", "HubSpot: Search for existing contact by email", 720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{contact_email}"}]}]',
            },
          ],
          variables: [
            {
              name: "total",
              customName: "existing_contact_count",
              type: "number",
            },
            {
              name: "results[0].properties.hs_object_id",
              customName: "hubspot_contact_id",
              type: "string",
            },
            {
              name: "results[0].properties.lifecyclestage",
              customName: "lifecycle_stage",
              type: "string",
            },
          ],
        },
      }),

      confidenceGate("5", "Gate: AI confidence >= 75?", 960, "75"),

      // TRUE path (high confidence) — auto-create lead + alert
      n("6", "hitApi", "HubSpot: Create or update lead contact", 1200, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"email":"{contact_email}","firstname":"{contact_name}","company":"{company_name}","jobtitle":"{job_title}","hs_lead_status":"NEW","lead_score":"{lead_score}","message":"{message_summary}"}',
            },
          ],
          variables: [
            { name: "id", customName: "new_contact_id", type: "string" },
            {
              name: "properties.email",
              customName: "created_email",
              type: "string",
            },
          ],
        },
      }),

      // FALSE path (low confidence) — SDR review
      approvalNode(
        "7",
        "Approval: SDR reviews low-confidence lead",
        1200,
        "AI confidence is below threshold for lead: {contact_name} at {company_name}. Score: {lead_score}, Tier: {qualification_tier}. Evidence: {ai_evidence}. Please review and approve or reject this lead for CRM entry.",
        24,
        400,
      ),

      verifyNode(
        "8",
        "Verify: Read back contact from HubSpot",
        1440,
        "GET",
        "https://api.hubapi.com/crm/v3/objects/contacts/{new_contact_id}?properties=email,firstname,company,hs_lead_status",
        [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
        [
          {
            name: "properties.email",
            customName: "verified_email",
            type: "string",
          },
          {
            name: "properties.hs_lead_status",
            customName: "verified_lead_status",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: Verification successful?",
        1680,
        "verify_status",
        "EQUALS",
        "200",
      ),

      sheetsNode(
        "10",
        "Google Sheets: Log lead capture audit trail",
        1920,
        "LeadCapture",
        '[["{case_id}","{contact_email}","{company_name}","{lead_score}","{qualification_tier}","{ai_confidence}","{ai_evidence}","{verified_lead_status}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Captures web form submissions, uses AI to extract and qualify leads with confidence scoring, auto-creates HubSpot contacts for high-confidence leads or routes to SDR review, verifies CRM entry, and logs the full audit trail.",
  },

  // ===================================================================
  // 2. Elite: Demo Request Triage + AE Routing
  // ===================================================================
  "Elite: Demo Request Triage + AE Routing": {
    nodes: [
      trigger("Webhook: Demo request form submitted", "webhook_form", {
        source: "Website",
        event: "demo.requested",
        howItStarts:
          "Fires when a prospect submits a demo request form, capturing company details, use case, and urgency indicators",
        setup:
          "Configure your demo request form to POST submissions to this webhook. Include fields for email, company, team size, use case, and timeline.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp Case: Generate case_id for demo request",
        240,
        "Demo Request Triage + AE Routing",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Classify urgency + fit score for demo request",
        480,
        "You are a sales operations expert specializing in demo request triage. Analyze the request to determine urgency, product fit, and ideal AE assignment based on company size, industry, and stated use case.",
        "Analyze this demo request: {{{1.body}}}. Return JSON with: requester_email, requester_name, company_name, team_size, use_case_summary, urgency_level (critical/high/medium/low), product_fit_score (0-100), ideal_ae_segment (enterprise/mid_market/smb), recommended_demo_type (live_custom/recorded_standard/self_serve).",
        [
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "team_size", as: "team_size", type: "string" },
          { field: "use_case_summary", as: "use_case_summary", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          {
            field: "product_fit_score",
            as: "product_fit_score",
            type: "number",
          },
          { field: "ideal_ae_segment", as: "ideal_ae_segment", type: "string" },
          {
            field: "recommended_demo_type",
            as: "recommended_demo_type",
            type: "string",
          },
        ],
      ),

      n(
        "4",
        "hitApi",
        "Clearbit: Enrich company data for firmographic context",
        720,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://company.clearbit.com/v2/companies/find?domain={company_name}",
            headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
            body: [],
            variables: [
              { name: "name", customName: "enriched_company", type: "string" },
              {
                name: "metrics.employees",
                customName: "employee_count",
                type: "number",
              },
              {
                name: "category.industry",
                customName: "industry",
                type: "string",
              },
              {
                name: "metrics.annualRevenue",
                customName: "annual_revenue",
                type: "string",
              },
            ],
          },
        },
      ),

      confidenceGate("5", "Gate: AI confidence >= 75?", 960, "75"),

      // TRUE path — auto-route to AE + alert
      slackNode(
        "6",
        "Slack: Alert assigned AE with demo brief",
        1200,
        ":calendar: *New Demo Request — Auto-Routed*\n*Requester:* {requester_name} ({requester_email})\n*Company:* {enriched_company} | {industry} | {employee_count} employees\n*Segment:* {ideal_ae_segment} | *Urgency:* {urgency_level}\n*Use Case:* {use_case_summary}\n*Recommended:* {recommended_demo_type}\n*Fit Score:* {product_fit_score} | *AI Confidence:* {ai_confidence}\n*Case:* {case_id}",
      ),

      // FALSE path — manager review
      approvalNode(
        "7",
        "Approval: Sales manager reviews ambiguous demo request",
        1200,
        "Demo request from {requester_name} at {company_name} needs review. AI confidence: {ai_confidence}. Urgency: {urgency_level}. Fit: {product_fit_score}. Evidence: {ai_evidence}. Please confirm routing to {ideal_ae_segment} segment.",
        12,
        400,
      ),

      verifyNode(
        "8",
        "Verify: Confirm CRM deal record was updated",
        1440,
        "GET",
        "https://api.hubapi.com/crm/v3/objects/contacts/search?query={requester_email}",
        [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        [
          { name: "total", customName: "contact_found", type: "number" },
          {
            name: "results[0].properties.hs_object_id",
            customName: "verified_contact_id",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: Demo routing confirmed?",
        1680,
        "verify_status",
        "EQUALS",
        "200",
      ),

      sheetsNode(
        "10",
        "Google Sheets: Log demo triage audit trail",
        1920,
        "DemoTriage",
        '[["{case_id}","{requester_email}","{company_name}","{urgency_level}","{ideal_ae_segment}","{product_fit_score}","{ai_confidence}","{recommended_demo_type}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Triages demo requests using AI to classify urgency and fit, enriches with Clearbit firmographics, auto-routes high-confidence requests to the right AE segment via Slack, sends ambiguous ones for manager approval, and logs the full audit trail.",
  },

  // ===================================================================
  // 3. Elite: Deal Stage Change Handoff
  // ===================================================================
  "Elite: Deal Stage Change Handoff": {
    nodes: [
      trigger("Webhook: HubSpot deal stage changed", "webhook_app", {
        source: "HubSpot",
        event: "deal.propertyChange",
        howItStarts:
          "Fires when a deal moves to a new pipeline stage in HubSpot, triggering handoff workflows between sales reps or teams",
        setup:
          "In HubSpot, create a workflow trigger on deal property change for 'Deal Stage'. Configure the webhook to fire on stage transitions and include deal_id, old_stage, new_stage, and owner_id in the payload.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp Case: Generate case_id for deal handoff",
        240,
        "Deal Stage Change Handoff",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Analyze deal context + determine handoff requirements",
        480,
        "You are a sales operations analyst. Analyze the deal stage transition to determine what handoff actions are needed, who the new owner should be, and what context the receiving rep needs. Consider deal value, timeline, and stage-specific requirements.",
        "Analyze this deal stage change: {{{1.body}}}. Return JSON with: deal_id, deal_name, deal_amount, old_stage, new_stage, current_owner_email, recommended_new_owner, handoff_priority (urgent/standard), required_actions (array of strings), context_brief (summary for new owner), risk_flags (any concerns about this transition).",
        [
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "string" },
          { field: "old_stage", as: "old_stage", type: "string" },
          { field: "new_stage", as: "new_stage", type: "string" },
          {
            field: "current_owner_email",
            as: "current_owner_email",
            type: "string",
          },
          {
            field: "recommended_new_owner",
            as: "recommended_new_owner",
            type: "string",
          },
          { field: "handoff_priority", as: "handoff_priority", type: "string" },
          { field: "context_brief", as: "context_brief", type: "string" },
          { field: "risk_flags", as: "risk_flags", type: "string" },
        ],
      ),

      n(
        "4",
        "hitApi",
        "HubSpot: Fetch deal details + associated contacts",
        720,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/deals/{deal_id}?associations=contacts&properties=dealname,amount,dealstage,hubspot_owner_id,closedate",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
            ],
            body: [],
            variables: [
              {
                name: "properties.dealname",
                customName: "verified_deal_name",
                type: "string",
              },
              {
                name: "properties.amount",
                customName: "verified_amount",
                type: "string",
              },
              {
                name: "properties.dealstage",
                customName: "current_stage",
                type: "string",
              },
              {
                name: "properties.hubspot_owner_id",
                customName: "current_owner_id",
                type: "string",
              },
            ],
          },
        },
      ),

      confidenceGate("5", "Gate: AI confidence >= 75?", 960, "75"),

      // TRUE path — auto-notify new owner
      slackNode(
        "6",
        "Slack: Notify new deal owner with handoff brief",
        1200,
        ":handshake: *Deal Handoff Alert*\n*Deal:* {deal_name} (${deal_amount})\n*Stage:* {old_stage} → {new_stage}\n*New Owner:* {recommended_new_owner}\n*Priority:* {handoff_priority}\n*Context:* {context_brief}\n*Risk Flags:* {risk_flags}\n*Case:* {case_id}",
      ),

      // FALSE path — manager approval
      approvalNode(
        "7",
        "Approval: Sales manager reviews handoff assignment",
        1200,
        "Deal handoff needs review: {deal_name} (${deal_amount}) moving from {old_stage} to {new_stage}. AI recommends {recommended_new_owner} as new owner. Confidence: {ai_confidence}. Risk flags: {risk_flags}. Please approve or reassign.",
        12,
        400,
      ),

      verifyNode(
        "8",
        "Verify: Confirm deal stage in HubSpot",
        1440,
        "GET",
        "https://api.hubapi.com/crm/v3/objects/deals/{deal_id}?properties=dealstage,hubspot_owner_id",
        [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
        [
          {
            name: "properties.dealstage",
            customName: "final_stage",
            type: "string",
          },
          {
            name: "properties.hubspot_owner_id",
            customName: "final_owner_id",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: Deal stage confirmed?",
        1680,
        "verify_status",
        "EQUALS",
        "200",
      ),

      sheetsNode(
        "10",
        "Google Sheets: Log deal handoff audit trail",
        1920,
        "DealHandoffs",
        '[["{case_id}","{deal_id}","{deal_name}","{deal_amount}","{old_stage}","{new_stage}","{recommended_new_owner}","{ai_confidence}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Manages deal stage transitions by analyzing handoff context with AI, auto-notifying new owners via Slack for high-confidence cases, routing ambiguous handoffs to manager approval, verifying the CRM state, and logging the audit trail.",
  },

  // ===================================================================
  // 4. Elite: Lost Deal Analysis + Recovery
  // ===================================================================
  "Elite: Lost Deal Analysis + Recovery": {
    nodes: [
      trigger("Webhook: HubSpot deal marked closed-lost", "webhook_app", {
        source: "HubSpot",
        event: "deal.closed_lost",
        howItStarts:
          "Fires when a deal is marked as closed-lost in HubSpot, initiating automated loss analysis and recovery outreach",
        setup:
          "In HubSpot, create a workflow trigger on deal stage change to 'Closed Lost'. Include deal_id, deal_name, amount, loss_reason, and associated contact email in the webhook payload.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp Case: Generate case_id for lost deal analysis",
        240,
        "Lost Deal Analysis + Recovery",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Analyze loss reasons + recommend recovery strategy",
        480,
        "You are a sales recovery strategist. Analyze the lost deal details to identify root causes, classify the loss reason, and recommend a specific recovery approach with timing. Consider whether the deal is recoverable and what messaging would work best.",
        "Analyze this lost deal: {{{1.body}}}. Return JSON with: deal_id, deal_name, deal_amount, primary_contact_email, contact_name, loss_category (price/competitor/timing/fit/no_decision/champion_left), loss_root_cause (detailed explanation), recovery_probability (0-100), recovery_strategy (specific approach), recovery_timing_days (recommended wait before outreach), follow_up_subject (email subject line), follow_up_body (personalized email body).",
        [
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "string" },
          {
            field: "primary_contact_email",
            as: "primary_contact_email",
            type: "string",
          },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "loss_category", as: "loss_category", type: "string" },
          { field: "loss_root_cause", as: "loss_root_cause", type: "string" },
          {
            field: "recovery_probability",
            as: "recovery_probability",
            type: "number",
          },
          {
            field: "recovery_strategy",
            as: "recovery_strategy",
            type: "string",
          },
          {
            field: "follow_up_subject",
            as: "follow_up_subject",
            type: "string",
          },
          { field: "follow_up_body", as: "follow_up_body", type: "string" },
        ],
      ),

      n(
        "4",
        "hitApi",
        "HubSpot: Fetch deal history + engagement timeline",
        720,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/deals/{deal_id}?properties=dealname,amount,dealstage,closed_lost_reason,notes_last_updated,hs_lastmodifieddate&associations=contacts",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
            ],
            body: [],
            variables: [
              {
                name: "properties.closed_lost_reason",
                customName: "crm_loss_reason",
                type: "string",
              },
              {
                name: "properties.amount",
                customName: "verified_amount",
                type: "string",
              },
              {
                name: "properties.hs_lastmodifieddate",
                customName: "last_activity",
                type: "string",
              },
            ],
          },
        },
      ),

      confidenceGate("5", "Gate: AI confidence >= 75?", 960, "75"),

      // TRUE path — send recovery email
      emailNode(
        "6",
        "SendGrid: Send personalized loss recovery follow-up",
        1200,
        "{primary_contact_email}",
        "{follow_up_subject}",
        "{follow_up_body}\n\n---\nCase Reference: {case_id}",
      ),

      // FALSE path — sales manager review
      approvalNode(
        "7",
        "Approval: Sales manager reviews recovery recommendation",
        1200,
        "Lost deal recovery needs review: {deal_name} (${deal_amount}). Loss category: {loss_category}. Root cause: {loss_root_cause}. Recovery probability: {recovery_probability}%. Strategy: {recovery_strategy}. AI confidence: {ai_confidence}. Please approve sending recovery email or modify approach.",
        48,
        400,
      ),

      verifyNode(
        "8",
        "Verify: Confirm recovery email was delivered",
        1440,
        "GET",
        "https://api.sendgrid.com/v3/messages?query=to_email%3D{primary_contact_email}&limit=1",
        [{ key: "Authorization", value: "Bearer {sendgrid_key}" }],
        [
          {
            name: "messages[0].status",
            customName: "email_status",
            type: "string",
          },
          {
            name: "messages[0].msg_id",
            customName: "email_msg_id",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: Email delivery confirmed?",
        1680,
        "verify_status",
        "EQUALS",
        "200",
      ),

      sheetsNode(
        "10",
        "Google Sheets: Log lost deal analysis audit",
        1920,
        "LostDeals",
        '[["{case_id}","{deal_id}","{deal_name}","{deal_amount}","{loss_category}","{loss_root_cause}","{recovery_probability}","{recovery_strategy}","{ai_confidence}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Analyzes closed-lost deals using AI to identify root causes and recovery strategies, auto-sends personalized follow-up emails for high-confidence cases, routes uncertain analyses to manager review, verifies delivery, and logs the complete audit trail.",
  },

  // ===================================================================
  // 5. Elite: Contract Signed to Onboarding
  // ===================================================================
  "Elite: Contract Signed to Onboarding": {
    nodes: [
      trigger("Webhook: Contract signed via DocuSign", "webhook_app", {
        source: "DocuSign",
        event: "envelope.completed",
        howItStarts:
          "Fires when a contract is fully signed in DocuSign, triggering automated onboarding setup including CRM update, task creation, and team notification",
        setup:
          "In DocuSign, configure a Connect webhook for envelope completion events. Include envelope_id, signer details, and contract metadata in the payload.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp Case: Generate case_id for onboarding kickoff",
        240,
        "Contract Signed to Onboarding",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Extract contract terms + generate onboarding plan",
        480,
        "You are a customer success onboarding planner. Parse the signed contract details to extract key terms, identify the customer tier, and generate a structured onboarding plan with milestones and task assignments.",
        "Parse this signed contract notification: {{{1.body}}}. Return JSON with: customer_email, customer_name, company_name, contract_value, contract_tier (enterprise/professional/starter), start_date, onboarding_tasks (comma-separated list of 5 key tasks), assigned_csm (based on tier), kickoff_call_urgency (within_24h/within_48h/within_week), special_requirements (any custom terms noted).",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "contract_value", as: "contract_value", type: "string" },
          { field: "contract_tier", as: "contract_tier", type: "string" },
          { field: "start_date", as: "start_date", type: "string" },
          { field: "onboarding_tasks", as: "onboarding_tasks", type: "string" },
          { field: "assigned_csm", as: "assigned_csm", type: "string" },
          {
            field: "kickoff_call_urgency",
            as: "kickoff_call_urgency",
            type: "string",
          },
          {
            field: "special_requirements",
            as: "special_requirements",
            type: "string",
          },
        ],
      ),

      n(
        "4",
        "hitApi",
        "HubSpot: Update deal to Closed Won + set onboarding properties",
        720,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.hubapi.com/crm/v3/objects/deals/{deal_id}",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"dealstage":"closedwon","amount":"{contract_value}","contract_tier":"{contract_tier}","onboarding_status":"pending","assigned_csm":"{assigned_csm}"}',
              },
            ],
            variables: [
              { name: "id", customName: "updated_deal_id", type: "string" },
              {
                name: "properties.dealstage",
                customName: "confirmed_stage",
                type: "string",
              },
            ],
          },
        },
      ),

      confidenceGate("5", "Gate: AI confidence >= 75?", 960, "75"),

      // TRUE path — auto-create onboarding tasks
      n("6", "hitApi", "Asana: Create onboarding project with tasks", 1200, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/projects",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"Onboarding: {company_name}","notes":"Contract: ${contract_value} | Tier: {contract_tier} | CSM: {assigned_csm} | Tasks: {onboarding_tasks} | Special: {special_requirements}","workspace":"{asana_workspace_id}"}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "project_gid", type: "string" },
            { name: "data.name", customName: "project_name", type: "string" },
          ],
        },
      }),

      // FALSE path — CS lead review
      approvalNode(
        "7",
        "Approval: CS lead reviews onboarding plan",
        1200,
        "New contract signed: {company_name} (${contract_value}, {contract_tier}). AI-generated onboarding plan needs review. Tasks: {onboarding_tasks}. Special requirements: {special_requirements}. AI confidence: {ai_confidence}. Please approve or modify the onboarding plan.",
        24,
        400,
      ),

      verifyNode(
        "8",
        "Verify: Confirm onboarding project was created in Asana",
        1440,
        "GET",
        "https://app.asana.com/api/1.0/projects/{project_gid}",
        [{ key: "Authorization", value: "Bearer {asana_token}" }],
        [
          {
            name: "data.name",
            customName: "verified_project_name",
            type: "string",
          },
          {
            name: "data.gid",
            customName: "verified_project_gid",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: Onboarding project verified?",
        1680,
        "verify_status",
        "EQUALS",
        "200",
      ),

      sheetsNode(
        "10",
        "Google Sheets: Log onboarding kickoff audit trail",
        1920,
        "Onboarding",
        '[["{case_id}","{company_name}","{contract_value}","{contract_tier}","{assigned_csm}","{kickoff_call_urgency}","{ai_confidence}","{project_gid}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Automates post-contract onboarding by extracting terms with AI, updating HubSpot to Closed Won, creating Asana onboarding projects for high-confidence cases, routing uncertain plans for CS lead review, and logging the full audit trail.",
  },

  // ===================================================================
  // 6. Elite: Lead Scoring with Enrichment
  // ===================================================================
  "Elite: Lead Scoring with Enrichment": {
    nodes: [
      trigger("Webhook: New lead captured from any channel", "webhook_app", {
        source: "CRM",
        event: "lead.created",
        howItStarts:
          "Fires when a new lead enters the CRM from any source (website, ad, referral, event), triggering AI-powered scoring with firmographic enrichment",
        setup:
          "Configure HubSpot or your CRM to send a webhook when a new contact is created with lifecycle stage 'Lead'. Include email, source, and any available properties.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp Case: Generate case_id for lead scoring",
        240,
        "Lead Scoring with Enrichment",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Score lead fit + intent based on available signals",
        480,
        "You are a revenue operations lead scoring expert. Analyze all available data about this lead to generate a composite score based on fit (firmographic match to ICP), intent (behavioral signals), and timing (buying stage indicators).",
        "Score this new lead: {{{1.body}}}. Return JSON with: lead_email, lead_name, company_domain, fit_score (0-100), intent_score (0-100), composite_score (0-100), scoring_rationale (explain the key factors), icp_match (strong/moderate/weak), recommended_cadence (fast_track/standard/nurture), priority_rank (P1/P2/P3).",
        [
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "company_domain", as: "company_domain", type: "string" },
          { field: "fit_score", as: "fit_score", type: "number" },
          { field: "intent_score", as: "intent_score", type: "number" },
          { field: "composite_score", as: "composite_score", type: "number" },
          {
            field: "scoring_rationale",
            as: "scoring_rationale",
            type: "string",
          },
          { field: "icp_match", as: "icp_match", type: "string" },
          {
            field: "recommended_cadence",
            as: "recommended_cadence",
            type: "string",
          },
          { field: "priority_rank", as: "priority_rank", type: "string" },
        ],
      ),

      n(
        "4",
        "hitApi",
        "Clearbit: Enrich lead with firmographic + technographic data",
        720,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://company.clearbit.com/v2/companies/find?domain={company_domain}",
            headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
            body: [],
            variables: [
              {
                name: "name",
                customName: "enriched_company_name",
                type: "string",
              },
              {
                name: "metrics.employees",
                customName: "employee_count",
                type: "number",
              },
              {
                name: "category.industry",
                customName: "industry",
                type: "string",
              },
              { name: "tech", customName: "tech_stack", type: "string" },
              {
                name: "metrics.annualRevenue",
                customName: "revenue_range",
                type: "string",
              },
            ],
          },
        },
      ),

      confidenceGate("5", "Gate: AI confidence >= 75?", 960, "75"),

      // TRUE path — update CRM with score + route
      n(
        "6",
        "hitApi",
        "HubSpot: Write lead score + route to correct cadence",
        1200,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{lead_email}?idProperty=email",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"lead_score":"{composite_score}","lead_priority":"{priority_rank}","lead_cadence":"{recommended_cadence}","icp_match":"{icp_match}","company":"{enriched_company_name}","industry":"{industry}","numberofemployees":"{employee_count}"}',
              },
            ],
            variables: [
              { name: "id", customName: "updated_contact_id", type: "string" },
              {
                name: "properties.lead_score",
                customName: "written_score",
                type: "string",
              },
            ],
          },
        },
      ),

      // FALSE path — RevOps review
      approvalNode(
        "7",
        "Approval: RevOps reviews low-confidence lead score",
        1200,
        "Lead scoring needs review: {lead_name} at {enriched_company_name} ({industry}, {employee_count} employees). AI scored composite: {composite_score}, fit: {fit_score}, intent: {intent_score}. Rationale: {scoring_rationale}. Confidence: {ai_confidence}. Please review and adjust.",
        24,
        400,
      ),

      verifyNode(
        "8",
        "Verify: Read back lead score from HubSpot",
        1440,
        "GET",
        "https://api.hubapi.com/crm/v3/objects/contacts/{updated_contact_id}?properties=lead_score,lead_priority,lead_cadence",
        [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
        [
          {
            name: "properties.lead_score",
            customName: "verified_score",
            type: "string",
          },
          {
            name: "properties.lead_priority",
            customName: "verified_priority",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: Score written successfully?",
        1680,
        "verify_status",
        "EQUALS",
        "200",
      ),

      sheetsNode(
        "10",
        "Google Sheets: Log lead scoring audit trail",
        1920,
        "LeadScoring",
        '[["{case_id}","{lead_email}","{enriched_company_name}","{composite_score}","{fit_score}","{intent_score}","{icp_match}","{priority_rank}","{ai_confidence}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Scores new leads using AI with composite fit+intent analysis, enriches with Clearbit firmographics, writes scores to HubSpot for high-confidence results, routes uncertain scores to RevOps review, verifies CRM updates, and logs the audit trail.",
  },

  // ===================================================================
  // 7. Elite: Meeting Prep Brief Generator
  // ===================================================================
  "Elite: Meeting Prep Brief Generator": {
    nodes: [
      trigger(
        "Webhook: Calendar meeting with prospect in 2 hours",
        "webhook_app",
        {
          source: "Calendly",
          event: "invitee.created",
          howItStarts:
            "Fires when a prospect meeting is scheduled or approaching on the calendar, generating a comprehensive prep brief for the sales rep with account research and talking points",
          setup:
            "Configure Calendly or Google Calendar webhook to fire when a meeting is created or 2 hours before a meeting with an external attendee. Include attendee email, name, company, and meeting type.",
          inputFields: [],
        },
      ),

      caseStampNode(
        "2",
        "Stamp Case: Generate case_id for meeting prep",
        240,
        "Meeting Prep Brief Generator",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Research account + generate meeting brief",
        480,
        "You are a sales intelligence analyst. Using the available data about the prospect and their company, generate a comprehensive meeting prep brief that includes company overview, recent news, likely pain points, competitive landscape, and recommended talking points.",
        "Generate a meeting prep brief for: {{{1.body}}}. Return JSON with: attendee_email, attendee_name, company_name, company_overview (2-3 sentences), recent_signals (notable company events), likely_pain_points (top 3), competitive_threats (known alternatives they may evaluate), talking_points (5 key discussion items), questions_to_ask (3 discovery questions), deal_positioning (recommended angle).",
        [
          { field: "attendee_email", as: "attendee_email", type: "string" },
          { field: "attendee_name", as: "attendee_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "company_overview", as: "company_overview", type: "string" },
          {
            field: "likely_pain_points",
            as: "likely_pain_points",
            type: "string",
          },
          { field: "talking_points", as: "talking_points", type: "string" },
          { field: "questions_to_ask", as: "questions_to_ask", type: "string" },
          { field: "deal_positioning", as: "deal_positioning", type: "string" },
          {
            field: "competitive_threats",
            as: "competitive_threats",
            type: "string",
          },
          { field: "recent_signals", as: "recent_signals", type: "string" },
        ],
      ),

      n(
        "4",
        "hitApi",
        "Clearbit + HubSpot: Enrich with firmographics and deal history",
        720,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://company.clearbit.com/v2/companies/find?domain={company_name}",
            headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
            body: [],
            variables: [
              { name: "name", customName: "enriched_company", type: "string" },
              {
                name: "metrics.employees",
                customName: "employee_count",
                type: "number",
              },
              {
                name: "category.industry",
                customName: "industry",
                type: "string",
              },
              {
                name: "metrics.annualRevenue",
                customName: "annual_revenue",
                type: "string",
              },
              {
                name: "description",
                customName: "company_description",
                type: "string",
              },
            ],
          },
        },
      ),

      confidenceGate("5", "Gate: AI confidence >= 75?", 960, "75"),

      // TRUE path — email brief to rep
      emailNode(
        "6",
        "SendGrid: Email meeting prep brief to sales rep",
        1200,
        "{rep_email}",
        "Meeting Prep Brief: {attendee_name} at {company_name} — {case_id}",
        "MEETING PREP BRIEF\n================\n\nAttendee: {attendee_name} ({attendee_email})\nCompany: {enriched_company} | {industry} | {employee_count} employees | {annual_revenue} revenue\n\nCOMPANY OVERVIEW:\n{company_overview}\n\nRECENT SIGNALS:\n{recent_signals}\n\nLIKELY PAIN POINTS:\n{likely_pain_points}\n\nCOMPETITIVE LANDSCAPE:\n{competitive_threats}\n\nTALKING POINTS:\n{talking_points}\n\nDISCOVERY QUESTIONS:\n{questions_to_ask}\n\nRECOMMENDED POSITIONING:\n{deal_positioning}\n\nAI Confidence: {ai_confidence}% | Evidence: {ai_evidence}",
      ),

      // FALSE path — rep edits needed
      approvalNode(
        "7",
        "Approval: Rep reviews AI-generated meeting brief",
        1200,
        "Meeting prep brief generated for {attendee_name} at {company_name} but AI confidence is low ({ai_confidence}). Key concern: {ai_evidence}. Please review the brief and approve for delivery or request revisions.",
        4,
        400,
      ),

      verifyNode(
        "8",
        "Verify: Confirm brief email was delivered",
        1440,
        "GET",
        "https://api.sendgrid.com/v3/messages?query=to_email%3D{rep_email}&limit=1",
        [{ key: "Authorization", value: "Bearer {sendgrid_key}" }],
        [
          {
            name: "messages[0].status",
            customName: "brief_delivery_status",
            type: "string",
          },
          {
            name: "messages[0].msg_id",
            customName: "brief_msg_id",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: Brief delivered successfully?",
        1680,
        "verify_status",
        "EQUALS",
        "200",
      ),

      sheetsNode(
        "10",
        "Google Sheets: Log meeting prep audit trail",
        1920,
        "MeetingPrep",
        '[["{case_id}","{attendee_email}","{company_name}","{industry}","{ai_confidence}","{deal_positioning}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Generates comprehensive meeting prep briefs using AI-powered account research, enriches with Clearbit firmographics, auto-delivers high-confidence briefs to reps via email, routes uncertain briefs for rep review, verifies delivery, and logs the audit trail.",
  },

  // ===================================================================
  // 8. Elite: Renewal Risk Assessment
  // ===================================================================
  "Elite: Renewal Risk Assessment": {
    nodes: [
      trigger("Schedule: Weekly renewal risk scan", "schedule", {
        schedule: "Every Monday at 8:00 AM",
        howItStarts:
          "Runs weekly to scan accounts approaching renewal, analyzing health signals, usage data, and engagement patterns to identify at-risk renewals before they churn",
        setup:
          "Set up a weekly scheduled trigger for Monday 8 AM. Ensure HubSpot account data includes renewal_date, usage_metrics, and support_ticket_count properties.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp Case: Generate case_id for renewal risk batch",
        240,
        "Renewal Risk Assessment",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Analyze health signals + usage patterns for risk",
        480,
        "You are a customer success risk analyst. Evaluate the account health signals including product usage trends, support ticket patterns, engagement frequency, and NPS/CSAT data. Classify the renewal risk and recommend specific intervention actions.",
        "Analyze renewal risk for accounts approaching renewal: {{{1.body}}}. Return JSON with: account_email, account_name, company_name, renewal_date, contract_value, usage_trend (increasing/stable/declining/critical), support_ticket_trend (normal/elevated/critical), engagement_score (0-100), risk_level (healthy/monitor/at_risk/critical), churn_probability (0-100), recommended_intervention (specific action plan), escalation_needed (boolean).",
        [
          { field: "account_email", as: "account_email", type: "string" },
          { field: "account_name", as: "account_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "renewal_date", as: "renewal_date", type: "string" },
          { field: "contract_value", as: "contract_value", type: "string" },
          { field: "usage_trend", as: "usage_trend", type: "string" },
          { field: "risk_level", as: "risk_level", type: "string" },
          {
            field: "churn_probability",
            as: "churn_probability",
            type: "number",
          },
          {
            field: "recommended_intervention",
            as: "recommended_intervention",
            type: "string",
          },
          {
            field: "escalation_needed",
            as: "escalation_needed",
            type: "string",
          },
        ],
      ),

      n(
        "4",
        "hitApi",
        "HubSpot: Fetch account details + renewal timeline",
        720,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/companies/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"renewal_date","operator":"WITHIN","value":"90"}]}]',
              },
            ],
            variables: [
              {
                name: "total",
                customName: "accounts_approaching_renewal",
                type: "number",
              },
              {
                name: "results[0].properties.name",
                customName: "top_risk_account",
                type: "string",
              },
              {
                name: "results[0].properties.renewal_date",
                customName: "nearest_renewal",
                type: "string",
              },
            ],
          },
        },
      ),

      confidenceGate("5", "Gate: AI confidence >= 75?", 960, "75"),

      // TRUE path — alert CSM + create task
      slackNode(
        "6",
        "Slack: Alert CSM with risk assessment + action items",
        1200,
        ":warning: *Renewal Risk Alert*\n*Account:* {company_name} ({account_email})\n*Renewal Date:* {renewal_date} | *Value:* ${contract_value}\n*Risk Level:* {risk_level} | *Churn Probability:* {churn_probability}%\n*Usage Trend:* {usage_trend}\n*Recommended Action:* {recommended_intervention}\n*AI Confidence:* {ai_confidence}% | *Case:* {case_id}",
      ),

      // FALSE path — CSM manual review
      approvalNode(
        "7",
        "Approval: CSM reviews uncertain risk assessment",
        1200,
        "Renewal risk assessment for {company_name} (${contract_value}) needs manual review. AI confidence: {ai_confidence}. Risk level: {risk_level}. Churn probability: {churn_probability}%. Usage trend: {usage_trend}. Evidence: {ai_evidence}. Please validate risk level and approve intervention plan.",
        24,
        400,
      ),

      verifyNode(
        "8",
        "Verify: Confirm risk task was created in CRM",
        1440,
        "GET",
        "https://api.hubapi.com/crm/v3/objects/tasks/search?query={company_name}",
        [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        [
          { name: "total", customName: "tasks_found", type: "number" },
          {
            name: "results[0].properties.hs_task_subject",
            customName: "task_subject",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: Risk alert delivered?",
        1680,
        "verify_status",
        "EQUALS",
        "200",
      ),

      sheetsNode(
        "10",
        "Google Sheets: Log renewal risk audit trail",
        1920,
        "RenewalRisk",
        '[["{case_id}","{company_name}","{contract_value}","{renewal_date}","{risk_level}","{churn_probability}","{usage_trend}","{recommended_intervention}","{ai_confidence}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Scans accounts approaching renewal weekly, uses AI to analyze health signals and predict churn risk, auto-alerts CSMs for high-confidence assessments, routes uncertain cases for manual review, verifies task creation, and logs the complete audit trail.",
  },

  // ===================================================================
  // 9. Elite: Referral Partner Follow-Up
  // ===================================================================
  "Elite: Referral Partner Follow-Up": {
    nodes: [
      trigger("Webhook: New referral submitted by partner", "webhook_app", {
        source: "PartnerStack",
        event: "referral.created",
        howItStarts:
          "Fires when a referral partner submits a new prospect referral, triggering qualification, CRM entry, and partner acknowledgment",
        setup:
          "Configure your partner portal (PartnerStack, Impact, or custom) to POST referral submissions to this webhook. Include partner_email, referred_email, referred_company, and referral_context.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp Case: Generate case_id for referral processing",
        240,
        "Referral Partner Follow-Up",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Qualify referral + assess partner reliability",
        480,
        "You are a partnerships operations specialist. Evaluate the referral quality based on the referred prospect details, the partner's track record, and alignment with your ICP. Determine if the referral is qualified and what follow-up is appropriate.",
        "Qualify this partner referral: {{{1.body}}}. Return JSON with: partner_email, partner_name, referred_email, referred_name, referred_company, referral_context, qualification_status (qualified/needs_more_info/unqualified), referral_quality_score (0-100), icp_alignment (strong/moderate/weak), recommended_action (immediate_outreach/nurture/pass), partner_thank_you_tier (gold/silver/bronze based on quality), follow_up_message (personalized message for referred prospect).",
        [
          { field: "partner_email", as: "partner_email", type: "string" },
          { field: "partner_name", as: "partner_name", type: "string" },
          { field: "referred_email", as: "referred_email", type: "string" },
          { field: "referred_name", as: "referred_name", type: "string" },
          { field: "referred_company", as: "referred_company", type: "string" },
          {
            field: "qualification_status",
            as: "qualification_status",
            type: "string",
          },
          {
            field: "referral_quality_score",
            as: "referral_quality_score",
            type: "number",
          },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          {
            field: "partner_thank_you_tier",
            as: "partner_thank_you_tier",
            type: "string",
          },
          {
            field: "follow_up_message",
            as: "follow_up_message",
            type: "string",
          },
        ],
      ),

      n(
        "4",
        "hitApi",
        "HubSpot: Create referral contact with partner attribution",
        720,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"email":"{referred_email}","firstname":"{referred_name}","company":"{referred_company}","hs_lead_status":"NEW","lead_source":"partner_referral","referral_partner":"{partner_name}","referral_quality":"{referral_quality_score}"}',
              },
            ],
            variables: [
              { name: "id", customName: "referral_contact_id", type: "string" },
              {
                name: "properties.email",
                customName: "created_referral_email",
                type: "string",
              },
            ],
          },
        },
      ),

      confidenceGate("5", "Gate: AI confidence >= 75?", 960, "75"),

      // TRUE path — send partner thank-you
      emailNode(
        "6",
        "SendGrid: Send partner thank-you acknowledgment",
        1200,
        "{partner_email}",
        "Thank you for referring {referred_name} at {referred_company}!",
        "Hi {partner_name},\n\nThank you for referring {referred_name} at {referred_company} to us! Your referral has been received and qualified.\n\nReferral Quality: {partner_thank_you_tier}\nStatus: {qualification_status}\nNext Steps: Our team will reach out to {referred_name} with a personalized introduction.\n\nWe truly value your partnership and will keep you updated on the progress of this referral.\n\nCase Reference: {case_id}\n\nBest regards,\nThe Partnerships Team",
      ),

      // FALSE path — partnerships team review
      approvalNode(
        "7",
        "Approval: Partnerships team reviews uncertain referral",
        1200,
        "Partner referral needs review: {partner_name} referred {referred_name} at {referred_company}. AI qualification: {qualification_status} (score: {referral_quality_score}). AI confidence: {ai_confidence}. Evidence: {ai_evidence}. Please confirm qualification and approve partner acknowledgment.",
        24,
        400,
      ),

      verifyNode(
        "8",
        "Verify: Confirm referral contact created in HubSpot",
        1440,
        "GET",
        "https://api.hubapi.com/crm/v3/objects/contacts/{referral_contact_id}?properties=email,lead_source,referral_partner",
        [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
        [
          {
            name: "properties.email",
            customName: "verified_referral_email",
            type: "string",
          },
          {
            name: "properties.lead_source",
            customName: "verified_source",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: Referral contact verified?",
        1680,
        "verify_status",
        "EQUALS",
        "200",
      ),

      sheetsNode(
        "10",
        "Google Sheets: Log referral processing audit trail",
        1920,
        "Referrals",
        '[["{case_id}","{partner_email}","{referred_email}","{referred_company}","{referral_quality_score}","{qualification_status}","{recommended_action}","{ai_confidence}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Processes partner referrals with AI-powered qualification scoring, creates CRM contacts with partner attribution, auto-sends partner thank-you emails for high-confidence referrals, routes uncertain ones for partnerships review, verifies CRM entry, and logs the audit trail.",
  },

  // ===================================================================
  // 10. Elite: Post-Call CRM Update
  // ===================================================================
  "Elite: Post-Call CRM Update": {
    nodes: [
      trigger(
        "Webhook: Call recording transcription completed",
        "webhook_app",
        {
          source: "Gong",
          event: "call.transcribed",
          howItStarts:
            "Fires when a sales call recording is transcribed by Gong or similar tool, triggering AI summarization, CRM note creation, and follow-up task generation",
          setup:
            "Configure Gong (or Chorus/Fireflies) to POST transcription webhooks when calls complete. Include call_id, transcript, attendees, and call metadata.",
          inputFields: [],
        },
      ),

      caseStampNode(
        "2",
        "Stamp Case: Generate case_id for call processing",
        240,
        "Post-Call CRM Update",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Summarize transcript + extract action items",
        480,
        "You are a sales call intelligence analyst. Parse the call transcript to generate a structured summary, extract key action items with owners, identify buying signals and objections, and update the deal stage recommendation. Focus on actionable insights, not play-by-play.",
        "Summarize this sales call transcript: {{{1.body}}}. Return JSON with: call_id, deal_name, prospect_email, prospect_name, call_duration_minutes, executive_summary (3-5 sentence summary), key_decisions (what was agreed), action_items (comma-separated with owners), buying_signals (positive indicators), objections_raised (concerns expressed), recommended_deal_stage (next appropriate stage), follow_up_date (recommended next touchpoint), sentiment (positive/neutral/negative).",
        [
          { field: "call_id", as: "call_id", type: "string" },
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "prospect_email", as: "prospect_email", type: "string" },
          { field: "prospect_name", as: "prospect_name", type: "string" },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "action_items", as: "action_items", type: "string" },
          { field: "buying_signals", as: "buying_signals", type: "string" },
          {
            field: "objections_raised",
            as: "objections_raised",
            type: "string",
          },
          {
            field: "recommended_deal_stage",
            as: "recommended_deal_stage",
            type: "string",
          },
          { field: "follow_up_date", as: "follow_up_date", type: "string" },
          { field: "sentiment", as: "sentiment", type: "string" },
        ],
      ),

      n("4", "hitApi", "HubSpot: Update deal notes with call summary", 720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/notes",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"hs_note_body":"CALL SUMMARY ({call_id})\\n{executive_summary}\\n\\nACTION ITEMS: {action_items}\\nBUYING SIGNALS: {buying_signals}\\nOBJECTIONS: {objections_raised}\\nSENTIMENT: {sentiment}\\nNEXT STEP: {recommended_deal_stage} by {follow_up_date}","hs_timestamp":"{started_at}"}',
            },
          ],
          variables: [{ name: "id", customName: "note_id", type: "string" }],
        },
      }),

      confidenceGate("5", "Gate: AI confidence >= 75?", 960, "75"),

      // TRUE path — notify rep + create tasks
      slackNode(
        "6",
        "Slack: Notify rep with call summary + action items",
        1200,
        ":phone: *Post-Call Summary Ready*\n*Deal:* {deal_name} | *Prospect:* {prospect_name}\n*Summary:* {executive_summary}\n*Action Items:* {action_items}\n*Buying Signals:* {buying_signals}\n*Objections:* {objections_raised}\n*Sentiment:* {sentiment}\n*Recommended Next:* {recommended_deal_stage} by {follow_up_date}\n*AI Confidence:* {ai_confidence}% | *Case:* {case_id}",
      ),

      // FALSE path — rep reviews summary
      approvalNode(
        "7",
        "Approval: Rep reviews AI-generated call summary",
        1200,
        "Call summary for {deal_name} with {prospect_name} needs your review. AI confidence: {ai_confidence}. Summary: {executive_summary}. Action items: {action_items}. The AI may have missed nuance. Please approve or edit before this is finalized in CRM.",
        12,
        400,
      ),

      verifyNode(
        "8",
        "Verify: Confirm notes saved to HubSpot",
        1440,
        "GET",
        "https://api.hubapi.com/crm/v3/objects/notes/{note_id}",
        [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
        [
          { name: "id", customName: "verified_note_id", type: "string" },
          {
            name: "properties.hs_note_body",
            customName: "verified_note_body",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: Notes saved successfully?",
        1680,
        "verify_status",
        "EQUALS",
        "200",
      ),

      sheetsNode(
        "10",
        "Google Sheets: Log post-call processing audit trail",
        1920,
        "CallSummaries",
        '[["{case_id}","{call_id}","{deal_name}","{prospect_email}","{sentiment}","{recommended_deal_stage}","{ai_confidence}","{note_id}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Processes call transcripts with AI to generate structured summaries, updates HubSpot deal notes, auto-notifies reps via Slack for high-confidence summaries, routes uncertain ones for rep review, verifies CRM notes were saved, and logs the audit trail.",
  },
};
